import React, {useState, useCallback, useEffect} from 'react';
import {View, StyleSheet, Image, ScrollView} from 'react-native';
import {
  Portal,
  Modal,
  Text,
  Button,
  IconButton,
  ActivityIndicator,
} from 'react-native-paper';
import {TouchableOpacity} from 'react-native';
import {launchImageLibrary} from 'react-native-image-picker';
import {colors, spacing} from '../../theme';
import {useOrderStore} from '../../store/useOrderStore';

const MAX_IMAGES = 2;

const OrderLineImagesModal = ({visible, onDismiss, orderId, line}) => {
  const {updateLine, uploadLineImages} = useOrderStore();
  const [clothImages, setClothImages] = useState([]);
  const [patternImages, setPatternImages] = useState([]);
  const [saving, setSaving] = useState(false);

  // Initialize images from line data when modal opens
  useEffect(() => {
    if (visible && line) {
      setClothImages(
        (line.clothImageUrls || []).map(url => ({uri: url, existing: true})),
      );
      setPatternImages(
        (line.patternImageUrls || []).map(url => ({uri: url, existing: true})),
      );
    }
  }, [visible, line]);

  const pickImage = useCallback(async setter => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (result.assets && result.assets.length > 0) {
      setter(prev => [...prev, result.assets[0]]);
    }
  }, []);

  const removeImage = useCallback((setter, idx) => {
    setter(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSave = useCallback(async () => {
    if (!orderId || !line) return;
    setSaving(true);
    try {
      const existingClothUrls = clothImages
        .filter(img => img.existing)
        .map(img => img.uri);
      const existingPatternUrls = patternImages
        .filter(img => img.existing)
        .map(img => img.uri);
      const newClothImages = clothImages.filter(img => !img.existing);
      const newPatternImages = patternImages.filter(img => !img.existing);

      // Check if any existing images were removed
      const origClothUrls = line.clothImageUrls || [];
      const origPatternUrls = line.patternImageUrls || [];
      const imagesDeleted =
        existingClothUrls.length !== origClothUrls.length ||
        existingPatternUrls.length !== origPatternUrls.length;

      // Only call updateLine if images were deleted — must include all required fields
      if (imagesDeleted) {
        await updateLine(orderId, line.id, {
          unitPrice: line.unitPrice,
          itemName: line.itemName,
          specialInstructions: line.specialInstructions,
          videoLink: line.videoLink,
          measurementDressGiven: line.measurementDressGiven,
          deliveryDate: line.deliveryDate,
          functionDate: line.functionDate,
          urgent: line.urgent,
          salesStaffId: line.salesStaffId,
          serviceCatalogItemId: line.serviceCatalogItemId,
          clothImageUrls: existingClothUrls,
          patternImageUrls: existingPatternUrls,
        });
      }

      // Upload newly picked images
      if (newClothImages.length > 0 || newPatternImages.length > 0) {
        await uploadLineImages(
          orderId,
          line.id,
          newClothImages.length > 0 ? newClothImages : null,
          newPatternImages.length > 0 ? newPatternImages : null,
        );
      }

      onDismiss(true); // true = refresh needed
    } catch (e) {
      // If upload fails, still close but signal refresh
      onDismiss(true);
    } finally {
      setSaving(false);
    }
  }, [orderId, line, clothImages, patternImages, updateLine, uploadLineImages, onDismiss]);

  if (!line) return null;

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={() => onDismiss(false)}
        contentContainerStyle={styles.modal}>
        <View style={styles.header}>
          <Text style={styles.title}>Images — {line.catalogItemName}</Text>
          <IconButton
            icon="close"
            size={20}
            onPress={() => onDismiss(false)}
            style={styles.closeBtn}
          />
        </View>

        <ScrollView style={styles.content}>
          {/* Cloth Images */}
          <Text style={styles.sectionLabel}>Cloth Images</Text>
          <View style={styles.imageRow}>
            {clothImages.map((img, idx) => (
              <View key={idx} style={styles.imageContainer}>
                <Image source={{uri: img.uri}} style={styles.imagePreview} />
                <TouchableOpacity
                  style={styles.imageDeleteBtn}
                  onPress={() => removeImage(setClothImages, idx)}>
                  <IconButton
                    icon="close-circle"
                    size={18}
                    iconColor={colors.error}
                    style={{margin: 0}}
                  />
                </TouchableOpacity>
              </View>
            ))}
            {clothImages.length < MAX_IMAGES && (
              <TouchableOpacity
                style={styles.imagePlaceholder}
                onPress={() => pickImage(setClothImages)}>
                <Text style={styles.imagePlaceholderText}>+ Add Photo</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Pattern Images */}
          <Text style={[styles.sectionLabel, {marginTop: spacing.md}]}>
            Pattern Images
          </Text>
          <View style={styles.imageRow}>
            {patternImages.map((img, idx) => (
              <View key={idx} style={styles.imageContainer}>
                <Image source={{uri: img.uri}} style={styles.imagePreview} />
                <TouchableOpacity
                  style={styles.imageDeleteBtn}
                  onPress={() => removeImage(setPatternImages, idx)}>
                  <IconButton
                    icon="close-circle"
                    size={18}
                    iconColor={colors.error}
                    style={{margin: 0}}
                  />
                </TouchableOpacity>
              </View>
            ))}
            {patternImages.length < MAX_IMAGES && (
              <TouchableOpacity
                style={styles.imagePlaceholder}
                onPress={() => pickImage(setPatternImages)}>
                <Text style={styles.imagePlaceholderText}>+ Add Photo</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <Button
            mode="outlined"
            onPress={() => onDismiss(false)}
            style={styles.cancelBtn}>
            Cancel
          </Button>
          <Button
            mode="contained"
            onPress={handleSave}
            loading={saving}
            disabled={saving}
            style={styles.saveBtn}>
            Save
          </Button>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.surface,
    margin: spacing.md,
    borderRadius: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    flex: 1,
  },
  closeBtn: {margin: 0},
  content: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  imageRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  imageContainer: {position: 'relative'},
  imagePreview: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: colors.border,
  },
  imageDeleteBtn: {position: 'absolute', top: -8, right: -8},
  imagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  imagePlaceholderText: {
    color: colors.textSecondary,
    fontSize: 11,
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  cancelBtn: {borderColor: colors.border},
  saveBtn: {backgroundColor: colors.primary},
});

export default OrderLineImagesModal;
